// Catálogo de motivos de desligamento (ex-aba Config_Motivos). A categoria do dashboard sai daqui:
// cadastrar um motivo que aparece no histórico da planilha mãe reclassifica esses desligamentos.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Pencil, Plus } from "lucide-react";
import { getRhDesligamentos, getRhMotivosDesligamento, postRhMotivoDesligamento, putRhMotivoDesligamento } from "../../api";
import { AvisoErro, BadgeAtivo, Campo, Modal, btnStyle, inputStyle, palette } from "../estoque/ui";
import { Secao } from "./formularios";
import RhLayout from "./RhLayout";

function ModalMotivo({ C, motivo, categorias, onClose, onSalvar }) {
  const [form, setForm] = useState({ motivo: motivo?.motivo || "", categoria: motivo?.categoria || "", ativo: motivo?.ativo ?? true });
  const [erro, setErro] = useState("");

  async function salvar() {
    if (!form.motivo.trim() || !form.categoria.trim()) return setErro("Preencha motivo e categoria.");
    try {
      await onSalvar(form);
    } catch (e) {
      setErro(e.message);
    }
  }

  return (
    <Modal C={C} titulo={motivo?.id ? "Editar motivo" : "Novo motivo"} onClose={onClose}
      rodape={<><button onClick={onClose} style={btnStyle(C.muted, true)}>Cancelar</button><button onClick={salvar} style={btnStyle(C.brand)}>Salvar</button></>}>
      <AvisoErro C={C} onClose={() => setErro("")}>{erro}</AvisoErro>
      <datalist id="rh-categorias-motivo">{categorias.map((c) => <option key={c} value={c} />)}</datalist>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Campo C={C} label="Motivo" obrigatorio>
          <input value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} style={inputStyle(C)} />
        </Campo>
        <Campo C={C} label="Categoria" obrigatorio ajuda="Escolha uma existente ou digite uma nova">
          <input value={form.categoria} list="rh-categorias-motivo" onChange={(e) => setForm({ ...form, categoria: e.target.value })} style={inputStyle(C)} />
        </Campo>
        <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}>
          <input type="checkbox" checked={form.ativo} onChange={(e) => setForm({ ...form, ativo: e.target.checked })} /> Ativo (aparece no registro de desligamento)
        </label>
      </div>
    </Modal>
  );
}

export default function MotivosView() {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const [motivos, setMotivos] = useState(null);
  const [semCategoria, setSemCategoria] = useState([]);
  const [erro, setErro] = useState("");
  const [modal, setModal] = useState(null); // null | {} (novo) | motivo

  const carregar = useCallback(() => {
    getRhMotivosDesligamento().then(setMotivos).catch((e) => setErro(e.message));
    getRhDesligamentos().then((d) => setSemCategoria(d.motivos_sem_categoria)).catch(() => {});
  }, []);
  useEffect(() => { carregar(); }, [carregar]);

  const categorias = useMemo(() => Array.from(new Set((motivos || []).map((m) => m.categoria))).sort((a, b) => a.localeCompare(b, "pt-BR")), [motivos]);

  async function salvar(form) {
    if (modal.id) await putRhMotivoDesligamento(modal.id, form);
    else await postRhMotivoDesligamento(form);
    setModal(null);
    carregar();
  }

  const th = { textAlign: "left", padding: "8px 12px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: C.muted, borderBottom: `1px solid ${C.border}` };
  const td = { padding: "8px 12px", fontSize: 13, borderBottom: `1px solid ${C.border}` };

  return (
    <RhLayout titulo="Motivos de desligamento" isLight={isLight} setIsLight={setIsLight}>
      <div style={{ maxWidth: 1000 }}>
        <AvisoErro C={C} onClose={() => setErro("")}>{erro}</AvisoErro>

        {semCategoria.length > 0 && (
          <Secao C={C} titulo={`Motivos do histórico sem categoria (${semCategoria.length})`}
            descricao="Textos de desligamentos antigos (planilha mãe) que não batem com o catálogo. Cadastre para classificá-los no dashboard.">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {semCategoria.map((m) => (
                <button key={m.motivo} onClick={() => setModal({ motivo: m.motivo })} title="Cadastrar este motivo"
                  style={{ display: "inline-flex", gap: 6, alignItems: "center", border: `1px dashed ${C.border}`, background: C.bg, color: C.text, borderRadius: 999, padding: "4px 10px", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>
                  <Plus size={12} /> {m.motivo} <span style={{ color: C.muted }}>({m.quantidade})</span>
                </button>
              ))}
            </div>
          </Secao>
        )}

        <Secao C={C} titulo="Catálogo" descricao="Motivo inativo some do registro de desligamento, mas continua classificando o histórico."
          acoes={<button onClick={() => setModal({})} style={btnStyle(C.brand)}><Plus size={15} /> Novo motivo</button>}>
          {!motivos ? <div style={{ fontSize: 13, color: C.muted }}>Carregando…</div> : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr><th style={th}>Categoria</th><th style={th}>Motivo</th><th style={{ ...th, textAlign: "center" }}>Usos</th><th style={{ ...th, textAlign: "center" }}>Situação</th><th style={th} /></tr></thead>
                <tbody>
                  {motivos.map((m) => (
                    <tr key={m.id}>
                      <td style={{ ...td, color: C.muted }}>{m.categoria}</td>
                      <td style={{ ...td, fontWeight: 600 }}>{m.motivo}</td>
                      <td style={{ ...td, textAlign: "center" }}>{m.qtd_usos}</td>
                      <td style={{ ...td, textAlign: "center" }}><BadgeAtivo C={C} ativo={m.ativo} /></td>
                      <td style={{ ...td, textAlign: "right" }}>
                        <button onClick={() => setModal(m)} title="Editar" style={{ background: "none", border: "none", color: C.muted, cursor: "pointer" }}><Pencil size={15} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Secao>
      </div>
      {modal && <ModalMotivo C={C} motivo={modal} categorias={categorias} onClose={() => setModal(null)} onSalvar={salvar} />}
    </RhLayout>
  );
}
